import React, { useEffect, useState, useRef, useMemo } from 'react';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Reply from '@mui/icons-material/Reply';
import axios from '../axiosConfig';
import { completeImageUrl } from '../utils/imageUrl';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import HeartIcon from '../components/HeartIcon';
import '../components/ProductsList.css';
import '../components/AuthPopup.css';
import './ProfilePage.css';
import ProductInfoContent from '../components/ProductInfoContent';
import { getProductImageUrl } from '../utils/productImage';
import { formatSizeSpaced } from '../utils/formatSize';
import FeedCaptionMeta from '../components/FeedCaptionMeta';


// Tea type of a product, from its category name ("Шу пуер", "Улун легкої ферментації" -> "пуер", "улун")
const TEA_GROUPS = [
  ['улун', 'улун'],
  ['пуер', 'пуер'],
  ['білий', 'білий'],
  ['червон', 'червоний'],
  ['зелен', 'зелений'],
  ['японськ', 'японський'],
  ['жовт', 'жовтий'],
  ['хей ча', 'хей ча'],
  ['набор', 'набори'],
];

function teaGroup(categoryName) {
  // NFC: some names spell "й" as "и" + a combining mark (e.g. "Хей Ча"), which would never match
  const name = (categoryName || '').normalize('NFC').toLowerCase();
  const match = TEA_GROUPS.find(([needle]) => name.includes(needle));
  return match ? match[1] : null;
}

// gift sets are not a tea type: they go last, in the circles and in the grid
const LAST_GROUP = 'набори';

function lowestPrice(product) {
  const prices = (product.product_variations || []).map((v) => parseFloat(v.price) || 0);
  return prices.length > 0 ? Math.min(...prices) : null;
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  // Check if accountName starts with @ or if pathname starts with /@
  const rawAccountName = params.accountName || '';
  const accountNameFromRoute = rawAccountName.startsWith('@') ? rawAccountName : (rawAccountName ? `@${rawAccountName}` : null);
  const isAccountPage = location.pathname === '/account';
  const [viewingAccountId, setViewingAccountId] = useState(null);
  const [currentUserAccountId, setCurrentUserAccountId] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [profileImageError, setProfileImageError] = useState(false);
  const [listMode, setListMode] = useState(false);
  const [displayProducts, setDisplayProducts] = useState([]);
  const [clickedProductId, setClickedProductId] = useState(null);
  const [likedIds, setLikedIds] = useState([]);
  // categories (with counts and a photo) and the price range, loaded on their own so the filters show up before the whole catalog has paged in
  const [categoryData, setCategoryData] = useState(null);
  // products matching the picked tea types / price range, asked from the backend (null while no filter is on)
  const [filteredProducts, setFilteredProducts] = useState(null);
  const [filterLoading, setFilterLoading] = useState(false);
  // the count shown as "Знайдено": set only when every page of a filter has arrived (null until the first filter finishes)
  const [settledCount, setSettledCount] = useState(null);
  const [cartIds, setCartIds] = useState([]);
  const [clickTimeouts, setClickTimeouts] = useState({});
  const [flippedProductId, setFlippedProductId] = useState(null);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isActionsExpanded, setIsActionsExpanded] = useState(false);
  const [priceFilter, setPriceFilter] = useState({ min: '', max: '' });
  const [appliedPriceFilter, setAppliedPriceFilter] = useState({ min: '', max: '' });
  const [selectedCategories, setSelectedCategories] = useState([]); // tea types picked in the circles (several allowed)
  const [followers, setFollowers] = useState(0);
  const [following, setFollowing] = useState(0);
  const [likes, setLikes] = useState(0);
  const [orders, setOrders] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showBuyDialog, setShowBuyDialog] = useState(false);
  const [buyDialogProduct, setBuyDialogProduct] = useState(null);
  const [buyDialogQuantity, setBuyDialogQuantity] = useState(1);
  const [toasts, setToasts] = useState([]);
  const clickedProductRef = useRef(null);
  const [profileTitle, setProfileTitle] = useState('tearoom');
  const [profileName, setProfileName] = useState('');
  const [profileDescription, setProfileDescription] = useState('');
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Prevent body scrolling when info is open
  useEffect(() => {
    const pageElement = document.querySelector('.products__page.profile-page--list-mode');
    const gridElement = document.querySelector('.products__page.profile-page--list-mode .products__grid');
    
    if (flippedProductId !== null) {
      // Store the current scroll position
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Lock scrolling on all scrollable elements
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollTop}px`;
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
      
      // Add class to page element to trigger CSS lock
      if (pageElement) {
        pageElement.classList.add('info-opened');
      }
      
      // Store scroll position on the grid element
      if (gridElement) {
        gridElement.dataset.scrollTop = gridElement.scrollTop.toString();
      }
    } else {
      // Restore the scroll position
      const scrollTop = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      
      if (scrollTop) {
        window.scrollTo(0, parseInt(scrollTop || '0') * -1);
      }
      
      // Remove class from page element
      if (pageElement) {
        pageElement.classList.remove('info-opened');
      }
      
      // Restore scroll position on the grid element
      if (gridElement && gridElement.dataset.scrollTop) {
        gridElement.scrollTop = parseInt(gridElement.dataset.scrollTop, 10);
        delete gridElement.dataset.scrollTop;
      }
    }
  }, [flippedProductId]);

  // Check for product query parameter and open it in list mode
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const productIdParam = searchParams.get('product');
    if (productIdParam) {
      // This will be handled after products are loaded
      const productId = parseInt(productIdParam, 10);
      if (!isNaN(productId)) {
        // Wait for products to load, then open the product
        const checkAndOpenProduct = () => {
          if (allProducts.length > 0 && !listMode) {
            const product = allProducts.find(p => p.id === productId);
            if (product) {
              setDisplayProducts(allProducts);
              setClickedProductId(productId);
              setListMode(true);
              // Remove query parameter from URL
              navigate(location.pathname, { replace: true });
            }
          }
        };
        
        // Check immediately and with delays
        checkAndOpenProduct();
        const timers = [
          setTimeout(checkAndOpenProduct, 100),
          setTimeout(checkAndOpenProduct, 500),
          setTimeout(checkAndOpenProduct, 1000)
        ];
        
        return () => {
          timers.forEach(timer => clearTimeout(timer));
        };
      }
    }
  }, [location.search, allProducts, listMode, navigate, location.pathname]);

  // Fetch all products once on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        
        // Determine which account we're viewing
        let accountId = null;
        let accountName = '';
        const token = localStorage.getItem('authToken');
        const currentIsAccountPage = location.pathname === '/account';
        const currentIsTearoomPage = location.pathname === '/@tearoom';
        // Check if pathname starts with /@ to determine if it's an account route
        const hasAccountNameRoute = accountNameFromRoute !== null && location.pathname.startsWith('/@');
        
        // Fetch current user's account_id
        if (token) {
          try {
            const userResponse = await axios.get('/api/v1/users/current');
            const userAccountId = userResponse.data.account_id;
            setCurrentUserAccountId(userAccountId);
            
            if (currentIsAccountPage) {
              // Viewing own account
              accountId = userAccountId;
            }
          } catch (userError) {
            console.error('Error fetching user data:', userError);
          }
        }
        
        // If we have an account name from route (e.g., /@x23), fetch account by name
        if (hasAccountNameRoute && !accountId) {
          try {
            const accountsResponse = await axios.get('/api/v1/accounts');
            const searchName = accountNameFromRoute.replace('@', '').toLowerCase();
            const foundAccount = accountsResponse.data.find(acc => {
              const accName = (acc.name || '').toLowerCase();
              const accSubdomain = (acc.subdomain || '').toLowerCase();
              // Also check if name includes @ prefix (like "@tearoom")
              const accNameWithAt = accName.startsWith('@') ? accName : `@${accName}`;
              const searchNameWithAt = `@${searchName}`;
              return accName === searchName || 
                     accSubdomain === searchName || 
                     accNameWithAt === searchNameWithAt ||
                     (accSubdomain === 'tearoom' && accountNameFromRoute.toLowerCase() === '@tearoom');
            });
            if (foundAccount) {
              accountId = foundAccount.id;
              setViewingAccountId(accountId);
            } else {
              console.error('Account not found for:', accountNameFromRoute);
            }
          } catch (error) {
            console.error('Error fetching account by name:', error);
          }
        }
        
        setViewingAccountId(accountId);
        
        // Track if profile image was set from account
        let profileImageSetFromAccount = false;
        
        // Fetch account details if we have an account ID (for /account route or account name route)
        if (accountId && (currentIsAccountPage || hasAccountNameRoute)) {
          try {
            const accountResponse = await axios.get(`/api/v1/accounts/${accountId}`);
            accountName = accountResponse.data.name || accountResponse.data.subdomain || '';
            setProfileName(accountName);
            const accountTitle = accountResponse.data.title || '';
            setProfileTitle(accountTitle);
            const description = accountResponse.data.description || '';
            setProfileDescription(description);
            
            // Update followers, following, and likes status
            if (accountResponse.data.followers_count !== undefined) {
              setFollowers(accountResponse.data.followers_count);
            }
            if (accountResponse.data.following_count !== undefined) {
              setFollowing(accountResponse.data.following_count);
            }
            if (accountResponse.data.likes_count !== undefined) {
              setLikes(accountResponse.data.likes_count);
            }
            if (accountResponse.data.orders_count !== undefined) {
              setOrders(accountResponse.data.orders_count);
            }
            // Always set following status from API response
            setIsFollowing(accountResponse.data.following === true);
            
            // Set profile image from account if available
            if (accountResponse.data.profile_image_url) {
              const profileImage = completeImageUrl(accountResponse.data.profile_image_url);
              setProfileImageUrl(profileImage);
              setProfileImageError(false);
              profileImageSetFromAccount = true;
            }
          } catch (accountError) {
            console.error('Error fetching account data:', accountError);
          }
        } else if (currentIsTearoomPage && !hasAccountNameRoute) {
          // Viewing tearoom account - fetch from database
          try {
            const accountsResponse = await axios.get('/api/v1/accounts');
            const tearoomAccount = accountsResponse.data.find(acc => acc.subdomain === 'tearoom');
            if (tearoomAccount) {
              accountId = tearoomAccount.id;
              setViewingAccountId(accountId);
              
              // Fetch full account details
              const accountResponse = await axios.get(`/api/v1/accounts/${accountId}`);
              const accountName = accountResponse.data.name || accountResponse.data.subdomain || '';
              setProfileName(accountName);
              const accountTitle = accountResponse.data.title || '';
              setProfileTitle(accountTitle);
              const description = accountResponse.data.description || '';
              setProfileDescription(description);
              
              // Update followers, following, and likes status
              if (accountResponse.data.followers_count !== undefined) {
                setFollowers(accountResponse.data.followers_count);
              }
              if (accountResponse.data.following_count !== undefined) {
                setFollowing(accountResponse.data.following_count);
              }
              if (accountResponse.data.likes_count !== undefined) {
                setLikes(accountResponse.data.likes_count);
              }
              if (accountResponse.data.orders_count !== undefined) {
                setOrders(accountResponse.data.orders_count);
              }
              if (accountResponse.data.following !== undefined) {
                setIsFollowing(accountResponse.data.following === true);
              }
              
              // Set profile image from account if available
              if (accountResponse.data.profile_image_url) {
                const profileImage = completeImageUrl(accountResponse.data.profile_image_url);
                setProfileImageUrl(profileImage);
                setProfileImageError(false);
                profileImageSetFromAccount = true;
              }
            } else {
              // Fallback if account not found
              setProfileName('@tearoom');
              setProfileTitle('TeaRoom');
              setProfileDescription('');
            }
          } catch (error) {
            console.error('Error fetching tearoom account:', error);
            // Fallback on error
            setProfileName('@tearoom');
            setProfileTitle('TeaRoom');
            setProfileDescription('');
          }
        }
        
        // Fetch products filtered by user's account_id if available
        // the API pages its list: show the first 20 right away, the rest arrives in the background
        const baseEndpoint = accountId ? `/api/v1/products?account_id=${accountId}&` : '/api/v1/products?';
        const pageSize = 20;
        axios
          .get(accountId ? `/api/v1/products/categories?account_id=${accountId}` : '/api/v1/products/categories')
          .then((response) => {
            if (!cancelled) setCategoryData(response.data);
          })
          .catch((error) => console.error('Error fetching categories:', error));
        const firstResponse = await axios.get(`${baseEndpoint}page=1&per_page=${pageSize}`);
        const fetchedProducts = firstResponse.data || [];
        if (cancelled) return;
        setAllProducts(fetchedProducts);
        setProducts(fetchedProducts);
        
        // Use account_url from first product only if profile_image_url was not set from account
        if (!profileImageSetFromAccount && fetchedProducts.length > 0) {
          const firstProduct = fetchedProducts[0];
          // Only use account_url from product, don't fallback to product image
          if (firstProduct.account_url) {
            const profileImage = completeImageUrl(firstProduct.account_url);
            setProfileImageUrl(profileImage);
            setProfileImageError(false);
            profileImageSetFromAccount = true;
          } else {
            // No account_url found, show placeholder (empty string)
            setProfileImageUrl('');
            setProfileImageError(false);
          }
        } else if (!profileImageSetFromAccount) {
          // No products and no account image, show placeholder
          setProfileImageUrl('');
          setProfileImageError(false);
        }
        
        // Initialize liked and cart state (the later pages add to it)
        const addLikedAndCart = (list, replace) => {
          if (!localStorage.getItem('authToken')) return;
          const liked = list.filter((p) => p.liked_by_current_user).map((p) => p.id);
          const inCart = list.filter((p) => p.in_cart).map((p) => p.id);
          setLikedIds((prev) => (replace ? liked : [...prev, ...liked]));
          setCartIds((prev) => (replace ? inCart : [...prev, ...inCart]));
        };
        if (fetchedProducts.length > 0) addLikedAndCart(fetchedProducts, true);
        setLoading(false);

        // the rest of the catalog (the grid, the circles and the price range need all of it)
        let lastBatchSize = fetchedProducts.length;
        for (let page = 2; lastBatchSize === pageSize; page += 1) {
          const response = await axios.get(`${baseEndpoint}page=${page}&per_page=${pageSize}`);
          if (cancelled) return;
          const batch = response.data || [];
          lastBatchSize = batch.length;
          if (batch.length > 0) {
            setAllProducts((prev) => [...prev, ...batch]);
            addLikedAndCart(batch, false);
          }
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    let cancelled = false;
    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, accountNameFromRoute]);

  // Any filter (tea types, price range) is asked from the backend: the first 20 matches at once, the rest page by page
  useEffect(() => {
    const hasPrice = appliedPriceFilter.min || appliedPriceFilter.max;
    if (selectedCategories.length === 0 && !hasPrice) {
      setFilteredProducts(null);
      setSettledCount(null);
      setFilterLoading(false);
      return undefined;
    }

    const params = new URLSearchParams();
    if (viewingAccountId) params.set('account_id', viewingAccountId);
    if (selectedCategories.length > 0) {
      const knownCategories = categoryData
        ? categoryData.categories
        : allProducts.map((product) => product.category_product).filter(Boolean);
      const categoryIds = [
        ...new Set(
          knownCategories
            .filter((category) => selectedCategories.includes(teaGroup(category.name)))
            .map((category) => category.id)
        ),
      ];
      if (categoryIds.length === 0) {
        setFilteredProducts([]);
        setSettledCount(0);
        setFilterLoading(false);
        return undefined;
      }
      params.set('category_ids', categoryIds.join(','));
    }
    if (appliedPriceFilter.min) params.set('price_from', appliedPriceFilter.min);
    if (appliedPriceFilter.max) params.set('price_to', appliedPriceFilter.max);

    let cancelled = false;
    // the list on screen stays until the first matches arrive (dimmed), instead of flashing empty
    setFilterLoading(true);
    const pageSize = 20;
    const pageUrl = (page) => `/api/v1/products?${params.toString()}&page=${page}&per_page=${pageSize}`;
    (async () => {
      try {
        const first = await axios.get(pageUrl(1));
        if (cancelled) return;
        const firstBatch = first.data || [];
        const total = parseInt(first.headers['x-total-count'], 10);
        // without the total header (an older backend) the pages are fetched one after another until a short one
        // nothing is shown yet: the dimmed list stays as it is until every page is in, then it is swapped in one go

        if (!Number.isNaN(total)) {
          // the total is known: fetch the remaining pages all at once instead of one after another
          const lastPage = Math.ceil(total / pageSize);
          const rest = await Promise.all(
            Array.from({ length: Math.max(0, lastPage - 1) }, (_, i) => axios.get(pageUrl(i + 2)))
          );
          if (cancelled) return;
          const all = [...firstBatch, ...rest.flatMap((response) => response.data || [])];
          setFilteredProducts(all);
          setSettledCount(all.length);
          setFilterLoading(false);
        } else {
          let all = firstBatch;
          let lastBatchSize = firstBatch.length;
          for (let page = 2; lastBatchSize === pageSize; page += 1) {
            const response = await axios.get(pageUrl(page));
            if (cancelled) return;
            const batch = response.data || [];
            lastBatchSize = batch.length;
            all = [...all, ...batch];
          }
          setFilteredProducts(all);
          setSettledCount(all.length);
          setFilterLoading(false);
        }
      } catch (error) {
        console.error('Error fetching filtered products:', error);
        if (!cancelled) setFilterLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // allProducts is only the fallback while the categories have not arrived: it must not restart the request
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories, appliedPriceFilter, categoryData, viewingAccountId]);

  // Filter products client-side when filters are applied
  useEffect(() => {
    // filtered products come from the backend; without a filter it is the whole catalog
    let filtered = [...(selectedCategories.length > 0 || appliedPriceFilter.min || appliedPriceFilter.max ? filteredProducts || allProducts : allProducts)];

    // sets at the end (a stable partition: the rest keeps the API order)
    filtered = [
      ...filtered.filter((product) => teaGroup(product.category_product?.name) !== LAST_GROUP),
      ...filtered.filter((product) => teaGroup(product.category_product?.name) === LAST_GROUP),
    ];

    setProducts(filtered);
    
    // Update profile image if needed - only use account_url from first product, not product image
    if (filtered.length > 0) {
      const firstProduct = filtered[0];
      if (firstProduct.account_url) {
        const profileImage = completeImageUrl(firstProduct.account_url);
        setProfileImageUrl(profileImage);
        setProfileImageError(false);
      } else {
        // No account_url, keep current profile image or show placeholder
        // Don't update if we already have a profile image from account
        if (!profileImageUrl) {
          setProfileImageUrl('');
          setProfileImageError(false);
        }
      }
    } else {
      // No filtered products, keep current profile image or show placeholder
      if (!profileImageUrl) {
        setProfileImageUrl('');
        setProfileImageError(false);
      }
    }
  }, [appliedPriceFilter, selectedCategories, filteredProducts, allProducts, profileImageUrl]);
  
  const openInfo = (product, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFlippedProductId(product.id);
  };

  const closeInfo = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFlippedProductId(null);
  };

  const refetchAccountStats = async () => {
    if (!viewingAccountId) return;
    
    try {
      const accountResponse = await axios.get(`/api/v1/accounts/${viewingAccountId}`);
      if (accountResponse.data.likes_count !== undefined) {
        setLikes(accountResponse.data.likes_count);
      }
    } catch (error) {
      console.error('Error refetching account stats:', error);
    }
  };

  const toggleLike = (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: window.location.pathname } })
      );
      return;
    }
    axios
      .post(`/api/v1/products/${id}/toggle_like`)
      .then((response) => {
        setLikedIds((prev) => {
          if (response.data.liked) {
            return [...prev, id];
          } else {
            return prev.filter((x) => x !== id);
          }
        });
        // Refetch account stats to update likes count
        refetchAccountStats();
      })
      .catch((error) => console.error('Error toggling like:', error));
  };

  const handleShare = async (productId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    const productUrl = `${window.location.origin}/products/${productId}`;
    
    try {
      // Try to use the Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(productUrl);
        addToast('Посилання скопійовано!');
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = productUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        addToast('Посилання скопійовано!');
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      addToast('Помилка копіювання');
    }
  };

  const toggleCart = (product, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: window.location.pathname } })
      );
      return;
    }
    // Show buy dialog instead of directly adding to cart
    setBuyDialogProduct(product);
    setBuyDialogQuantity(1);
    setShowBuyDialog(true);
  };

  const addToast = (message) => {
    const toastId = Date.now() + Math.random();
    const newToast = {
      id: toastId,
      message: message,
      hiding: false
    };
    
    setToasts(prev => [...prev, newToast]);
    
    // Hide toast after 2.5 seconds
    setTimeout(() => {
      setToasts(prev => prev.map(toast => 
        toast.id === toastId ? { ...toast, hiding: true } : toast
      ));
      
      // Remove toast after animation
      setTimeout(() => {
        setToasts(prev => prev.filter(toast => toast.id !== toastId));
      }, 300);
    }, 2500);
  };

  const handleBuyDialogConfirm = () => {
    if (!buyDialogProduct || !buyDialogProduct.product_variations || buyDialogProduct.product_variations.length === 0) {
      return;
    }
    
    // Check if user is trying to buy from themselves
    if (buyDialogProduct.account_id && currentUserAccountId && 
        String(buyDialogProduct.account_id) === String(currentUserAccountId)) {
      addToast('Покупка у себе не дозволена правилами');
      return;
    }
    
    const quantity = buyDialogQuantity || 0;
    if (quantity <= 0) {
      addToast('Введіть кількість');
      return;
    }
    
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: window.location.pathname } })
      );
      setShowBuyDialog(false);
      return;
    }

    const variation = buyDialogProduct.product_variations.find(v => v.main) || buyDialogProduct.product_variations[0];
    if (!variation) {
      addToast('Помилка: варіанти товару недоступні');
      return;
    }
    
    // Quantity is number of portions (each portion is 10g)
    const itemsToAdd = quantity;
    
    // Add to cart with quantity - make multiple API calls like ProductsList
    const addToCartPromises = Array.from({ length: itemsToAdd }, () =>
      axios.post(`/api/v1/cart/add_to_cart`, { product_variation_id: variation.id })
    );
    
    Promise.all(addToCartPromises)
      .then(() => {
        setCartIds((prev) => {
          if (!prev.includes(buyDialogProduct.id)) {
            return [...prev, buyDialogProduct.id];
          }
          return prev;
        });
        setShowBuyDialog(false);
        addToast(`Додано ${quantity} порцій (${quantity * 10}g) в кошик!`);
        window.dispatchEvent(new CustomEvent('cartUpdated'));
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        addToast('Помилка додавання в кошик');
        setShowBuyDialog(false);
      });
  };

  const handleMobileCardClick = (productId, e) => {
    // Detect mobile device
    const isMobile = window.innerWidth <= 768;
    
    if (!isMobile) {
      // Desktop behavior - allow navigation (but we don't navigate in profile list mode)
      return;
    }
    
    // Mobile behavior - prevent navigation and handle double-click for like
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    const lastClick = clickTimeouts[productId];
    
    if (lastClick && now - lastClick < 300) {
      // Double click detected - toggle like
      clearTimeout(clickTimeouts[`timeout_${productId}`]);
      toggleLike(productId, e);
      setClickTimeouts(prev => {
        const { [productId]: removed, [`timeout_${productId}`]: removedTimeout, ...rest } = prev;
        return rest;
      });
    } else {
      // Single click - do nothing, just update timestamp
      setClickTimeouts(prev => ({
        ...prev,
        [productId]: now
      }));
      
      // Set timeout to clear the click timestamp
      const timeoutId = setTimeout(() => {
        setClickTimeouts(prev => {
          const { [productId]: removed, [`timeout_${productId}`]: removedTimeout, ...rest } = prev;
          return rest;
        });
      }, 300);
      
      setClickTimeouts(prev => ({
        ...prev,
        [`timeout_${productId}`]: timeoutId
      }));
    }
  };


  const handleProductClick = (productId, productIndex) => {
    // Keep original order - don't reorder products
    setDisplayProducts(products);
    setClickedProductId(productId);
    setListMode(true);
  };

  // Listen for custom events from Header
  useEffect(() => {
    const checkListMode = () => {
      if (listMode) {
        window.dispatchEvent(new CustomEvent('profileListModeResponse'));
      }
    };
    
    const exitListMode = () => {
      if (listMode) {
    setListMode(false);
    setDisplayProducts([]);
    setClickedProductId(null);
    setFlippedProductId(null);
      }
    };
    
    window.addEventListener('checkProfileListMode', checkListMode);
    window.addEventListener('exitProfileListMode', exitListMode);
    
    return () => {
      window.removeEventListener('checkProfileListMode', checkListMode);
      window.removeEventListener('exitProfileListMode', exitListMode);
    };
  }, [listMode]);

  // Notify Header when list mode changes
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('profileListModeChange', { detail: { listMode } }));
  }, [listMode]);

  // Handle swipe back gesture on mobile when in list mode
  const minSwipeDistance = 50;
  const swipeEdgeThreshold = 30; // Start swipe from left edge (30px)

  const onTouchStart = (e) => {
    if (!listMode) return;
    const touch = e.touches[0];
    // Only detect swipe if starting from left edge
    if (touch.clientX <= swipeEdgeThreshold) {
      setTouchEnd(null);
      setTouchStart(touch.clientX);
    }
  };

  const onTouchMove = (e) => {
    if (!listMode) return;
    if (touchStart !== null && touchStart <= swipeEdgeThreshold) {
      const currentX = e.touches[0].clientX;
      setTouchEnd(currentX);
      
      // If swiping right from edge, prevent default browser back gesture
      if (currentX > touchStart) {
        e.preventDefault();
      }
    }
  };

  const onTouchEnd = (e) => {
    if (!listMode) {
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    if (!touchStart || !touchEnd) {
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    const distance = touchEnd - touchStart;
    const isRightSwipe = distance > minSwipeDistance;
    
    // Swipe right (from left edge) to exit list mode
    if (isRightSwipe && touchStart <= swipeEdgeThreshold) {
      // Prevent browser's native back gesture
      e.preventDefault();
      // Exit list mode to go back to grid view
      window.dispatchEvent(new CustomEvent('exitProfileListMode'));
    }
    
    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleFollow = async () => {
    if (!viewingAccountId) return;
    
    const token = localStorage.getItem('authToken');
    if (!token) {
      // Open auth popup if not logged in
      window.dispatchEvent(new CustomEvent('openAuthPopup'));
      return;
    }

    try {
      let response;
      if (isFollowing) {
        // Unfollow
        response = await axios.delete(`/api/v1/accounts/${viewingAccountId}/follow`);
      } else {
        // Follow
        response = await axios.post(`/api/v1/accounts/${viewingAccountId}/follow`);
      }
      
      // Immediately update state from response
      if (response && response.data) {
        console.log('Follow response:', response.data);
        if (typeof response.data.followers_count === 'number') {
          setFollowers(response.data.followers_count);
        }
        // Force boolean conversion and use functional update to ensure React detects change
        const newFollowingState = response.data.following === true;
        console.log('Setting following to:', newFollowingState);
        setIsFollowing(newFollowingState);
        
        // Reset expanded state when unfollowing
        if (!newFollowingState) {
          setIsActionsExpanded(false);
        }
      }
      
      // Small delay then refetch to ensure consistency
      setTimeout(async () => {
        try {
          const accountResponse = await axios.get(`/api/v1/accounts/${viewingAccountId}`);
          if (accountResponse && accountResponse.data) {
            if (typeof accountResponse.data.followers_count === 'number') {
              setFollowers(accountResponse.data.followers_count);
            }
            setIsFollowing(accountResponse.data.following === true);
          }
        } catch (refetchError) {
          console.error('Error refetching account data:', refetchError);
        }
      }, 100);
    } catch (error) {
      console.error('Error toggling follow:', error);
      if (error.response?.status === 401) {
        // Not authenticated, open auth popup
        window.dispatchEvent(new CustomEvent('openAuthPopup'));
      }
    }
  };

  const handleMessage = () => {
    window.open('https://t.me/tearoom', '_blank');
  };

  // the filter circle only opens/closes the price panel; applied filters are shown (and cleared) in the chips row
  const handleFilterClick = () => {
    setIsFilterExpanded(prev => !prev);
  };

  const handlePriceChange = (type, value) => {
    setPriceFilter(prev => ({
      ...prev,
      [type]: value === '' ? '' : Number(value)
    }));
  };

  const handleApplyFilter = () => {
    setAppliedPriceFilter({ ...priceFilter });
    setIsFilterExpanded(false);
    // Products will be refetched automatically via useEffect when appliedPriceFilter changes
  };

  const handleClearFilter = () => {
    setPriceFilter({ min: '', max: '' });
    setAppliedPriceFilter({ min: '', max: '' });
    setIsFilterExpanded(false);
    // Products will be refetched automatically via useEffect when appliedPriceFilter changes
  };

  const hasActiveFilters = appliedPriceFilter.min || appliedPriceFilter.max;

  // tap a tea type to add it (products of all picked types are shown), tap again to remove it
  const toggleCategory = (key) => {
    setSelectedCategories((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleResetAll = () => {
    setSelectedCategories([]);
    handleClearFilter();
  };

  // one circle per tea type that this shop really sells (built from all products, not the filtered ones)
  const categoryGroups = useMemo(() => {
    const groups = new Map();
    // the category data knows the whole catalog at once; until they arrive, build from the products loaded so far
    const entries = categoryData
      ? categoryData.categories.map((category) => ({
          key: teaGroup(category.name),
          count: category.count,
          image: category.attachment ? getProductImageUrl({ attachments: [category.attachment] }) : null,
        }))
      : allProducts.map((product) => ({
          key: teaGroup(product.category_product?.name),
          count: 1,
          image: getProductImageUrl(product),
        }));
    entries.forEach(({ key, count, image }) => {
      if (!key) return;
      const group = groups.get(key) || { key, count: 0, image: null };
      group.count += count;
      if (!group.image) group.image = image;
      groups.set(key, group);
    });
    return [...groups.values()].sort((a, b) => {
      if ((a.key === LAST_GROUP) !== (b.key === LAST_GROUP)) return a.key === LAST_GROUP ? 1 : -1;
      return b.count - a.count;
    });
  }, [categoryData, allProducts]);

  // the shop's real price range, shown as the placeholders of the price inputs
  const priceBounds = useMemo(() => {
    if (categoryData && categoryData.price_min != null) return { min: categoryData.price_min, max: categoryData.price_max };
    const prices = allProducts.map(lowestPrice).filter((price) => price !== null && price > 0);
    return prices.length > 0 ? { min: Math.min(...prices), max: Math.max(...prices) } : null;
  }, [categoryData, allProducts]);

  // "Знайдено" changes (and first shows up) only when every page of the filter has arrived and the dimming is gone
  const foundCount = settledCount;

  const priceChipLabel = [
    appliedPriceFilter.min ? `від ${appliedPriceFilter.min}` : null,
    appliedPriceFilter.max ? `до ${appliedPriceFilter.max}` : null,
  ].filter(Boolean).join(' ') + ' ₴';

  // Scroll to clicked product when entering list mode
  useEffect(() => {
    if (listMode && displayProducts.length > 0 && clickedProductId) {
      const scrollToProduct = () => {
        const gridContainer = document.querySelector('.products__grid');
        if (!gridContainer) return;
        
        if (clickedProductRef.current) {
          const isMobile = window.innerWidth <= 768;
          if (isMobile) {
            // For mobile TikTok-style view, scroll the grid container to show clicked product
            const productTop = clickedProductRef.current.offsetTop;
            gridContainer.scrollTo({ top: productTop, behavior: 'instant' });
          } else {
            // Desktop: scroll to center the product
            clickedProductRef.current.scrollIntoView({ behavior: 'instant', block: 'center' });
          }
        }
      };
      
      // Scroll immediately and with delays to catch all rendering scenarios
      scrollToProduct();
      const timers = [
        setTimeout(scrollToProduct, 50),
        setTimeout(scrollToProduct, 150),
        setTimeout(scrollToProduct, 300),
        setTimeout(scrollToProduct, 500)
      ];
      
      return () => {
        timers.forEach(timer => clearTimeout(timer));
      };
    }
  }, [listMode, clickedProductId, displayProducts.length]);

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-indicator">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  // TikTok-style list mode - use exact same structure as ProductsList
  if (listMode && displayProducts.length > 0) {
    return (
      <div 
        className="products__page profile-page--list-mode"
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="profile-list-header">
          <div className="profile-list-header-info">
              {profileImageUrl && !profileImageError ? (
            <span className="profile-list-header-avatar">
              <img
              src={profileImageUrl} 
                alt={profileName || profileTitle}
              className="profile-list-header-avatar__img"
                onError={() => setProfileImageError(true)}
              />
            </span>
            ) : (
              <span className="profile-list-header-image profile-list-header-image--placeholder" title={profileTitle || profileName}>
                <span className="profile-list-header-image__name">{(profileTitle || profileName).replace(/^@/, '')}</span>
              </span>
            )}
            <div className="profile-list-header-title-wrapper">
              {profileTitle && (
            <span className="profile-list-header-title">{profileTitle}</span>
              )}
              {profileName && (
                <span className="profile-list-header-name">{profileName}</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="products__grid">
          {displayProducts.map((product, index) => {
            const imageUrl = getProductImageUrl(product);
            const isLiked = likedIds.includes(product.id);
            const isFlipped = flippedProductId === product.id;
            const isClickedProduct = product.id === clickedProductId;
            
            return (
              <div 
                key={product.id} 
                className="products__link"
                ref={isClickedProduct ? clickedProductRef : null}
                onClick={(e) => handleMobileCardClick(product.id, e)}
              >
                <div className={`products__item ${isFlipped ? 'products__item--flipped' : ''}`}>
                  <div
                    className="products__item-background"
                    style={{ backgroundImage: `url(${imageUrl})` }}
                  />
                  <div
                    className="products__item-foreground"
                    style={{ backgroundImage: `url(${imageUrl})` }}
                  />
                  {/* dark fade under the caption, same as the home feed */}
                  <div className="feed-caption-scrim" aria-hidden="true" />
                  {/* Profile button */}
                  <div className="products__corner products__corner-topright products__corner-topright-new">
                    <button
                      className="products__new-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        // Exit list mode to go back to account/profile grid view
                        if (listMode) {
                          window.dispatchEvent(new CustomEvent('exitProfileListMode'));
                        } else {
                          // Navigate to the appropriate profile page based on current route
                          if (isAccountPage) {
                            navigate('/account');
                          } else {
                            navigate('/@tearoom');
                          }
                        }
                      }}
                      aria-label="Back to profile"
                      style={product.account_url ? {
                        backgroundImage: `url(${completeImageUrl(product.account_url)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.4)',
                        backdropFilter: 'blur(4px)'
                      }}
                    >
                      {!product.account_url && (
                        <span className="products__new-toggle-name">{product.account_title || (product.account_name || '').replace(/^@/, '') || 'Магазин'}</span>
                      )}
                    </button>
                  </div>
                  {/* Info button */}
                  <div className="products__corner products__corner-topright products__corner-topright-info">
                    <button
                      className="products__info-toggle"
                      onClick={(e) => isFlipped ? closeInfo(e) : openInfo(product, e)}
                      aria-label={isFlipped ? "Close info" : "Product info"}
                    >
                      {isFlipped ? '×' : <VisibilityOutlined className="products__info-icon-svg" />}
                    </button>
                  </div>
                  {/* Like button */}
                  <div className="products__corner products__corner-topright">
                    <button
                      className={`products__toggle ${isLiked ? 'liked' : 'unliked'}`}
                      onClick={(e) => toggleLike(product.id, e)}
                    >
                      <HeartIcon />
                    </button>
                  </div>
                  {/* Share button */}
                  <div className="products__corner products__corner-middle-right">
                    <button
                      className="products__share-toggle"
                      onClick={(e) => handleShare(product.id, e)}
                      aria-label="Share product"
                    >
                      <Reply className="products__share-icon" style={{ transform: 'scaleX(-1)' }} />
                    </button>
                  </div>
                  {/* Cart button */}
                  <div className="products__corner products__corner-bottomright">
                          <button
                      className={`products__cart-toggle ${cartIds.includes(product.id) ? 'inCart' : 'outCart'}`}
                      onClick={(e) => toggleCart(product, e)}
                    >
                      <ShoppingCartIcon className="products__cart-icon" />
                          </button>
                  </div>
                  {/* Product name and price caption - same markup as the home feed (ProductsList) */}
                  {product.product_variations && product.product_variations.length > 0 && (
                    <div className="feed-caption">
                      <span className="feed-caption__name">
                        {product.title}
                      </span>
                      <FeedCaptionMeta variation={product.product_variations.find(v => v.main) || product.product_variations[0]} />
                    </div>
                  )}
                  {/* Info card back */}
                  <div className="products__item-back">
                    <ProductInfoContent product={product} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {showBuyDialog && buyDialogProduct && (
        <div className="auth-popup-overlay" onClick={() => setShowBuyDialog(false)}>
          <div className="auth-popup-content" onClick={(e) => e.stopPropagation()} style={{ padding: 0 }}>
            <div style={{ textAlign: 'center', paddingTop: '50px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '20px' }}>
                  <button
                    onClick={() => {
                      const newQty = (buyDialogQuantity || 0) - 1;
                      setBuyDialogQuantity(Math.max(0, newQty));
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '2px solid #000',
                      borderRadius: '50%',
                      background: '#fff',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      padding: 0
                    }}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="0"
                    value={buyDialogQuantity || ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                      setBuyDialogQuantity(Math.max(0, val));
                    }}
                    style={{
                      width: '60px',
                      height: '40px',
                      border: '2px solid #000',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      padding: '0 8px'
                    }}
                  />
                  <button
                    onClick={() => {
                      const newQty = (buyDialogQuantity || 0) + 1;
                      setBuyDialogQuantity(newQty);
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '2px solid #000',
                      borderRadius: '50%',
                      background: '#fff',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      padding: 0
                    }}
                  >
                    +
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    visibility: (buyDialogQuantity || 0) > 0 ? 'visible' : 'hidden'
                  }}>
                    {(() => {
                      const quantity = buyDialogQuantity || 0;
                      const grams = quantity * 10;
                      return grams > 0 ? `${grams}G` : '0';
                    })()}
                  </div>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: 'bold', 
                    color: 'var(--color-accent)',
                    visibility: (buyDialogQuantity || 0) > 0 ? 'visible' : 'hidden'
                  }}>
                    {(() => {
                      const variation = buyDialogProduct.product_variations.find(v => v.main) || buyDialogProduct.product_variations[0];
                      if (!variation) return '0';
                      const basePrice = parseFloat(variation.price) || 0;
                      const quantity = buyDialogQuantity || 0;
                      // Quantity is number of portions (each portion is 10g)
                      const totalPrice = quantity * basePrice;
                      return totalPrice % 1 === 0 ? totalPrice.toString() : totalPrice.toFixed(2);
                    })()} ₴
                  </div>
                </div>
                {buyDialogProduct && buyDialogProduct.account_id && currentUserAccountId && 
                 String(buyDialogProduct.account_id) === String(currentUserAccountId) && (
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--color-accent)',
                    marginTop: '16px',
                    textAlign: 'center',
                    padding: '8px 16px',
                    backgroundColor: '#fff5f8',
                    borderRadius: '8px'
                  }}>
                    Покупка у себе не дозволена правилами
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleBuyDialogConfirm();
                  }}
                  disabled={(buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? '#ccc' : 'var(--color-accent)',
                    border: 'none',
                    cursor: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 'none' : '0 4px 12px rgba(31, 31, 31, 0.3)',
                    transition: 'all 0.3s ease',
                    marginRight: '20px',
                    marginBottom: '20px',
                    opacity: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if ((buyDialogQuantity || 0) > 0 && !(buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) {
                      e.currentTarget.style.background = 'var(--color-accent-hover)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if ((buyDialogQuantity || 0) > 0 && !(buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) {
                      e.currentTarget.style.background = 'var(--color-accent)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                >
                  <ShoppingCartIcon style={{ color: '#fff', fontSize: '28px' }} />
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
        {toasts.map((toast, index) => (
          <div 
            key={toast.id}
            className={`toast-notification ${toast.hiding ? 'toast-hiding' : ''}`}
            style={{ top: `${20 + index * 70}px` }}
          >
            <span>{toast.message}</span>
          </div>
        ))}
      </div>
    );
  }

  // Grid mode
  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-image-container">
          {profileImageUrl && !profileImageError ? (
          <img 
            src={profileImageUrl} 
              alt={profileName || profileTitle}
            className="profile-image"
              onError={() => setProfileImageError(true)}
            />
          ) : (
            <span className="profile-image-placeholder">
              <span className="profile-image-placeholder__name">{profileTitle || profileName}</span>
            </span>
          )}
        </div>
        {profileTitle && (
        <h1 className="profile-title">{profileTitle}</h1>
        )}
        {profileName && (
          <div className="profile-name">{profileName}</div>
        )}
        
        {/* Stats */}
        <div className="profile-stats">
          <div className="profile-stat">
            <span className="profile-stat-value">{followers}</span>
            <span className="profile-stat-label">слідкують</span>
          </div>
          <div className="profile-stat">
            <span className="profile-stat-value">{following}</span>
            <span className="profile-stat-label">слідкує</span>
          </div>
          <div className="profile-stat">
            <span className="profile-stat-value">{likes}</span>
            <span className="profile-stat-label">вподобання</span>
          </div>
          <div className="profile-stat">
            <span className="profile-stat-value">{orders}</span>
            <span className="profile-stat-label">замовлення</span>
          </div>
        </div>
        
        {/* Action buttons */}
        {!isAccountPage && viewingAccountId !== currentUserAccountId && (
        <div className="profile-actions">
            {isFollowing ? (
              <>
                <div className="profile-actions-row">
                  <button 
                    className="profile-button profile-button--expand"
                    onClick={() => setIsActionsExpanded(!isActionsExpanded)}
                    aria-label="Expand actions"
                  >
                    {isActionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </button>
                  <button 
                    className="profile-button profile-button--message"
                    onClick={handleMessage}
                  >
                    Написати
                  </button>
                </div>
                {isActionsExpanded && (
                  <button 
                    className="profile-button profile-button--unfollow"
                    onClick={handleFollow}
                  >
                    Не слідкувати
                  </button>
                )}
              </>
            ) : (
              <div className="profile-actions-row">
          <button 
            className="profile-button profile-button--message"
            onClick={handleMessage}
          >
            Написати
          </button>
          <button 
            className="profile-button profile-button--follow"
            onClick={handleFollow}
          >
            Слідкувати
          </button>
        </div>
            )}
          </div>
        )}
        
        {/* Description */}
        {profileDescription && (
        <div className="profile-description">
            {profileDescription.split('\n').filter(line => line.trim()).map((line, index) => (
              <p key={index}>{line.trim()}</p>
            ))}
        </div>
        )}
        
        {/* Highlights: the price filter button + one circle per tea type (tap to filter, tap again to clear) */}
        {allProducts.length > 0 && (
        <div className="profile-highlights">
          <div className="profile-highlight" onClick={handleFilterClick}>
            <div className={`profile-highlight-circle profile-highlight-circle--filter ${isFilterExpanded ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}>
              <FilterListIcon className="profile-highlight-icon" />
            </div>
            <span className="profile-highlight-title">Фільтри</span>
          </div>
          {categoryGroups.map((group) => (
            <div
              key={group.key}
              className={`profile-highlight ${selectedCategories.includes(group.key) ? 'profile-highlight--selected' : ''}`}
              onClick={() => toggleCategory(group.key)}
              role="button"
              aria-pressed={selectedCategories.includes(group.key)}
            >
              <div className="profile-highlight-circle">
                {group.image && (
                  <img src={group.image} alt={group.key} className="profile-highlight-image" />
                )}
              </div>
              <span className="profile-highlight-title">{group.key}</span>
            </div>
          ))}
        </div>
        )}

        {/* Price filter panel */}
        <div className={`profile-filter-expanded ${isFilterExpanded ? 'open' : ''}`}>
          <div className="profile-filter-content">
            <h3 className="profile-filter-title">Ціна</h3>
            <div className="profile-filter-price-inputs">
              <label className="profile-filter-price-input">
                <span className="profile-filter-caption">Від</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  placeholder={priceBounds ? String(priceBounds.min) : '0'}
                  value={priceFilter.min || ''}
                  onChange={(e) => handlePriceChange('min', e.target.value)}
                  className="profile-filter-input"
                />
                <span className="profile-filter-currency">₴</span>
              </label>
              <span className="profile-filter-separator">—</span>
              <label className="profile-filter-price-input">
                <span className="profile-filter-caption">До</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  placeholder={priceBounds ? String(priceBounds.max) : ''}
                  value={priceFilter.max || ''}
                  onChange={(e) => handlePriceChange('max', e.target.value)}
                  className="profile-filter-input"
                />
                <span className="profile-filter-currency">₴</span>
              </label>
            </div>
            <div className="profile-filter-actions">
              <button
                type="button"
                className="profile-filter-button profile-filter-button--clear"
                onClick={handleClearFilter}
              >
                Скинути
              </button>
              <button
                type="button"
                className="profile-filter-button profile-filter-button--apply"
                onClick={handleApplyFilter}
              >
                Застосувати
              </button>
            </div>
          </div>
        </div>

        {/* What is applied right now: tap a chip to remove that filter */}
        {(selectedCategories.length > 0 || hasActiveFilters) && (
          <div className="profile-active-filters">
            <div className="profile-active-filters__top">
              <span className="profile-active-filters__count" style={{ visibility: foundCount === null ? 'hidden' : 'visible' }}>
                Знайдено: {foundCount === null ? 0 : foundCount}
              </span>
              <button type="button" className="profile-active-filters__reset" onClick={handleResetAll}>
                Скинути все
              </button>
            </div>
            <div className="profile-active-filters__chips">
              {selectedCategories.map((key) => (
                <button key={key} type="button" className="profile-active-chip" onClick={() => toggleCategory(key)}>
                  {key}<span aria-hidden="true"> ×</span>
                </button>
              ))}
              {hasActiveFilters && (
                <button type="button" className="profile-active-chip" onClick={handleClearFilter}>
                  {priceChipLabel}<span aria-hidden="true"> ×</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      
      <div className="profile-products-grid">
        {/* while a filter is being fetched: placeholder cards instead of the old list */}
        {filterLoading && Array.from({ length: 8 }, (_, i) => (
          <div key={`skeleton-${i}`} className="profile-product-item profile-product-item--skeleton" aria-hidden="true">
            <div className="profile-product-thumb">
              <div className="profile-product-skeleton-lines">
                <span />
                <span />
              </div>
            </div>
          </div>
        ))}
        {!filterLoading && products.map((product, index) => {
          const imageUrl = getProductImageUrl(product);
          // plain price: the lowest one (the main/smallest size), no "від"
          const prices = (product.product_variations || []).map((v) => parseFloat(v.price)).filter((n) => n > 0);
          const minPrice = prices.length > 0 ? Math.min(...prices) : null;
          // size of that cheapest variation, shown next to the price
          const cheapest = (product.product_variations || []).find((v) => parseFloat(v.price) === minPrice);
          const minSize = cheapest && cheapest.size ? formatSizeSpaced(cheapest.size) : null;
          return (
            <div
              key={product.id}
              className="profile-product-item"
              onClick={() => handleProductClick(product.id, index)}
            >
              <div className="profile-product-thumb">
                <img className="profile-product-image" src={imageUrl} alt="" loading="lazy" decoding="async" />
                <div className="profile-product-info">
                  <span className="profile-product-name">{product.title}</span>
                  {minPrice !== null && (
                    <span className="profile-product-price">
{minPrice % 1 === 0 ? minPrice : minPrice.toFixed(2)} ₴
{minSize && <span className="profile-product-size">{minSize}</span>}
</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {showBuyDialog && buyDialogProduct && (
        <div className="auth-popup-overlay" onClick={() => setShowBuyDialog(false)}>
          <div className="auth-popup-content" onClick={(e) => e.stopPropagation()} style={{ padding: 0 }}>
            <div style={{ textAlign: 'center', paddingTop: '50px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '20px' }}>
                  <button
                    onClick={() => {
                      const newQty = (buyDialogQuantity || 0) - 1;
                      setBuyDialogQuantity(Math.max(0, newQty));
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '2px solid #000',
                      borderRadius: '50%',
                      background: '#fff',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      padding: 0
                    }}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="0"
                    value={buyDialogQuantity || ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                      setBuyDialogQuantity(Math.max(0, val));
                    }}
                    style={{
                      width: '60px',
                      height: '40px',
                      border: '2px solid #000',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      padding: '0 8px'
                    }}
                  />
                  <button
                    onClick={() => {
                      const newQty = (buyDialogQuantity || 0) + 1;
                      setBuyDialogQuantity(newQty);
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '2px solid #000',
                      borderRadius: '50%',
                      background: '#fff',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      padding: 0
                    }}
                  >
                    +
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    visibility: (buyDialogQuantity || 0) > 0 ? 'visible' : 'hidden'
                  }}>
                    {(() => {
                      const quantity = buyDialogQuantity || 0;
                      const grams = quantity * 10;
                      return grams > 0 ? `${grams}G` : '0';
                    })()}
                  </div>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: 'bold', 
                    color: 'var(--color-accent)',
                    visibility: (buyDialogQuantity || 0) > 0 ? 'visible' : 'hidden'
                  }}>
                    {(() => {
                      const variation = buyDialogProduct.product_variations.find(v => v.main) || buyDialogProduct.product_variations[0];
                      if (!variation) return '0';
                      const basePrice = parseFloat(variation.price) || 0;
                      const quantity = buyDialogQuantity || 0;
                      // Quantity is number of portions (each portion is 10g)
                      const totalPrice = quantity * basePrice;
                      return totalPrice % 1 === 0 ? totalPrice.toString() : totalPrice.toFixed(2);
                    })()} ₴
                  </div>
                </div>
                {buyDialogProduct && buyDialogProduct.account_id && currentUserAccountId && 
                 String(buyDialogProduct.account_id) === String(currentUserAccountId) && (
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--color-accent)',
                    marginTop: '16px',
                    textAlign: 'center',
                    padding: '8px 16px',
                    backgroundColor: '#fff5f8',
                    borderRadius: '8px'
                  }}>
                    Покупка у себе не дозволена правилами
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleBuyDialogConfirm();
                  }}
                  disabled={(buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? '#ccc' : 'var(--color-accent)',
                    border: 'none',
                    cursor: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 'none' : '0 4px 12px rgba(31, 31, 31, 0.3)',
                    transition: 'all 0.3s ease',
                    marginRight: '20px',
                    marginBottom: '20px',
                    opacity: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if ((buyDialogQuantity || 0) > 0 && !(buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) {
                      e.currentTarget.style.background = 'var(--color-accent-hover)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if ((buyDialogQuantity || 0) > 0 && !(buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) {
                      e.currentTarget.style.background = 'var(--color-accent)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                >
                  <ShoppingCartIcon style={{ color: '#fff', fontSize: '28px' }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {toasts.map((toast, index) => (
        <div 
          key={toast.id}
          className={`toast-notification ${toast.hiding ? 'toast-hiding' : ''}`}
          style={{ top: `${20 + index * 70}px` }}
        >
          <span>{toast.message}</span>
        </div>
      ))}
    </div>
  );
}

